import React from "react";
import Select, { Props as SelectProps, GroupBase } from "react-select";
import { theme } from "@/style";

export interface CustomSelectOption {
    label: string;
    value: string | number;
}

export interface CustomSelectProps
    extends Omit<
        SelectProps<CustomSelectOption, boolean, GroupBase<CustomSelectOption>>,
        "options"
    > {
    label?: string;
    error?: string;
    options: CustomSelectOption[];
    isMulti?: boolean;
    placeholder?: string;
    isSearchable?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    required?: boolean;
    containerClassName?: string;
    containerStyle?: React.CSSProperties;
    selectClassName?: string;
    selectStyle?: React.CSSProperties;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
    label,
    error,
    options,
    isMulti = false,
    placeholder = "Select...",
    isSearchable = true,
    isDisabled = false,
    isLoading = false,
    required = false,
    containerClassName,
    containerStyle,
    selectClassName,
    selectStyle,
    ...props
}) => {
    const customStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            minHeight: "40px",
            maxHeight: "100px",
            overflowY: "auto",
            border: state.isFocused
                ? `1px solid ${theme.PrimaryColor}`
                : error
                ? `1px solid ${theme.PrimaryRed}`
                : `1px solid ${theme.PrimaryGreyLight}`,
            borderRadius: "8px",
            boxShadow: state.isFocused ? `0 0 0 1px ${theme.PrimaryColor}` : "none",
            backgroundColor: isDisabled ? theme.PrimaryGreyLight : "white",
            "&:hover": {
                border: state.isFocused
                    ? `1px solid ${theme.PrimaryColor}`
                    : `1px solid ${theme.PrimaryGreyMid}`,
            },
        }),
        menu: (provided: any) => ({
            ...provided,
            border: `1px solid ${theme.PrimaryGreyLight}`,
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            zIndex: 9999,
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? theme.PrimaryColor
                : state.isFocused
                ? theme.PrimaryGreyLight
                : "white",
            color: state.isSelected ? "white" : theme.PrimaryGreyDark,
            padding: "12px 16px",
            cursor: "pointer",
            "&:hover": {
                backgroundColor: state.isSelected ? theme.PrimaryColor : theme.PrimaryGreyLight,
            },
        }),
        multiValue: (provided: any) => ({
            ...provided,
            backgroundColor: theme.PrimaryColor,
            borderRadius: "6px",
            margin: "2px",
        }),
        multiValueLabel: (provided: any) => ({
            ...provided,
            color: "white",
            fontWeight: "500",
            fontSize: "12px",
        }),
        multiValueRemove: (provided: any) => ({
            ...provided,
            color: "white",
            ":hover": {
                backgroundColor: theme.PrimaryRed,
                color: "white",
            },
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: theme.PrimaryGreyMid,
            fontSize: "14px",
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: theme.PrimaryGreyDark,
            fontSize: "14px",
        }),
        input: (provided: any) => ({
            ...provided,
            color: theme.PrimaryGreyDark,
            fontSize: "14px",
        }),
        indicatorSeparator: (provided: any) => ({
            ...provided,
            backgroundColor: theme.PrimaryGreyLight,
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: theme.PrimaryGreyMid,
            "&:hover": {
                color: theme.PrimaryGreyDark,
            },
        }),
        clearIndicator: (provided: any) => ({
            ...provided,
            color: theme.PrimaryGreyMid,
            "&:hover": {
                color: theme.PrimaryRed,
            },
        }),
    };

    return (
        <div
            className={containerClassName}
            style={{
                width: "100%",
                ...containerStyle,
            }}
        >
            {label && (
                <label
                    style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "500",
                        fontSize: "14px",
                        color: theme.PrimaryGreyDark,
                        lineHeight: "1.2",
                    }}
                >
                    {label}
                    {required && (
                        <span style={{ color: theme.PrimaryRed, marginLeft: "4px" }}>*</span>
                    )}
                </label>
            )}
            <Select
                className={selectClassName}
                style={selectStyle}
                options={options}
                isMulti={isMulti}
                placeholder={placeholder}
                isSearchable={isSearchable}
                isDisabled={isDisabled}
                isLoading={isLoading}
                closeMenuOnSelect={!isMulti}
                hideSelectedOptions={false}
                styles={customStyles}
                noOptionsMessage={() => "No options available"}
                loadingMessage={() => "Loading..."}
                {...props}
            />
            {error && (
                <div
                    style={{
                        color: theme.PrimaryRed,
                        fontSize: "12px",
                        marginTop: "4px",
                        fontWeight: "400",
                    }}
                >
                    {error}
                </div>
            )}
        </div>
    );
};
